import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  History,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  FileText,
  Clock,
  User,
  Package,
  AlertCircle,
  FileCheck,
  Shirt,
  Scissors,
  Table2,
  Settings,
  Eye,
  Play
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CargarTarifasExcel = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  // Estados
  const [archivo, setArchivo] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [paso, setPaso] = useState(1); // 1: Seleccionar, 2: Validar, 3: Previsualizar, 4: Confirmar
  const [cargando, setCargando] = useState(false);
  const [validacion, setValidacion] = useState(null);
  const [previsualizacion, setPrevisualizacion] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [mostrarHistorial, setMostrarHistorial] = useState(false);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [accionHuerfanas, setAccionHuerfanas] = useState('mantener');
  const [mostrarErrores, setMostrarErrores] = useState(false);
  const [mostrarConfiguracion, setMostrarConfiguracion] = useState(false);

  // Verificar permisos
  const esAdmin = user?.role === 'admin' || user?.role === 'gerencia';

  // Cargar historial al montar
  useEffect(() => {
    if (esAdmin) {
      cargarHistorial();
    }
  }, [esAdmin]);

  // Manejar drag and drop
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setArrastrando(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setArrastrando(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setArrastrando(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      validarYSeleccionarArchivo(files[0]);
    }
  }, []);

  const validarYSeleccionarArchivo = (file) => {
    if (!file.name.endsWith('.xlsx')) {
      toast.error('Solo se permiten archivos Excel (.xlsx)');
      return;
    }
    setArchivo(file);
    setValidacion(null);
    setPrevisualizacion(null);
    setResultado(null);
    setPaso(1);
    toast.success(`Archivo seleccionado: ${file.name}`);
  };

  const handleFileSelect = (e) => {
    if (e.target.files.length > 0) {
      validarYSeleccionarArchivo(e.target.files[0]);
    }
  };

  // Cargar historial
  const cargarHistorial = async () => {
    setCargandoHistorial(true);
    try {
      const response = await axios.get(`${API_URL}/api/cotizador/importador-oficial/historial`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistorial(response.data || []);
    } catch (error) {
      console.error('Error cargando historial:', error);
    } finally {
      setCargandoHistorial(false);
    }
  };

  // PASO 2: Validar estructura del archivo
  const validarArchivo = async () => {
    if (!archivo) {
      toast.error('Seleccione un archivo primero');
      return;
    }

    setCargando(true);
    setValidacion(null);

    try {
      const formData = new FormData();
      formData.append('file', archivo);

      const response = await axios.post(
        `${API_URL}/api/cotizador/importador-oficial/validar`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setValidacion(response.data);
      
      if (response.data.es_valido) {
        toast.success('Archivo válido - Estructura correcta');
        setPaso(2);
      } else {
        toast.error('El archivo no tiene la estructura correcta');
      }
    } catch (error) {
      console.error('Error validando:', error);
      toast.error(error.response?.data?.detail || 'Error validando archivo');
    } finally {
      setCargando(false);
    }
  };

  // PASO 3: Previsualizar cambios
  const previsualizarCambios = async () => {
    if (!archivo) return;

    setCargando(true);
    setPrevisualizacion(null);

    try {
      const formData = new FormData();
      formData.append('file', archivo);

      const response = await axios.post(
        `${API_URL}/api/cotizador/importador-oficial/previsualizar`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setPrevisualizacion(response.data);
      setPaso(3);
      toast.success('Vista previa generada');
    } catch (error) {
      console.error('Error previsualizando:', error);
      toast.error(error.response?.data?.detail || 'Error generando vista previa');
    } finally {
      setCargando(false);
    }
  };

  // PASO 4: Confirmar importación
  const confirmarImportacion = async () => {
    if (!archivo) return;

    setCargando(true);
    setResultado(null);

    try {
      const formData = new FormData();
      formData.append('file', archivo);

      const response = await axios.post(
        `${API_URL}/api/cotizador/importador-oficial/importar?accion_huerfanas=${accionHuerfanas}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setResultado(response.data);
      setPaso(4);
      toast.success('Importación completada exitosamente');
      cargarHistorial();
    } catch (error) {
      console.error('Error importando:', error);
      toast.error(error.response?.data?.detail || 'Error importando tarifas');
    } finally {
      setCargando(false);
    }
  };

  // Reiniciar proceso
  const reiniciar = () => {
    setArchivo(null);
    setValidacion(null);
    setPrevisualizacion(null);
    setResultado(null);
    setPaso(1);
    setMostrarErrores(false);
    setMostrarConfiguracion(false);
  };

  // Formatear número
  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-CO').format(num || 0);
  };

  // Formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!esAdmin) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800">Acceso Restringido</h2>
            <p className="text-red-600 mt-2">Solo administradores pueden importar tarifas</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/quotes')}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Importar Tarifas</h1>
              <p className="text-muted-foreground">Formato oficial: Hoja TARIFICADOR</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                window.open(`${API_URL}/api/cotizador/importador-oficial/plantilla`, '_blank');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4" />
              Descargar Plantilla
            </button>
            <button
              onClick={() => setMostrarHistorial(!mostrarHistorial)}
              data-testid="historial-toggle-btn"
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <History className="w-4 h-4" />
              Historial
              {mostrarHistorial ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Indicador de pasos */}
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Seleccionar archivo', icon: Upload },
              { num: 2, label: 'Validar estructura', icon: FileCheck },
              { num: 3, label: 'Vista previa', icon: Eye },
              { num: 4, label: 'Confirmar', icon: Play }
            ].map((step, idx) => (
              <React.Fragment key={step.num}>
                <div className={`flex items-center gap-2 ${paso >= step.num ? 'text-primary' : 'text-muted-foreground'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    paso > step.num ? 'bg-primary text-white' : 
                    paso === step.num ? 'bg-primary/20 text-primary border-2 border-primary' : 
                    'bg-muted text-muted-foreground'
                  }`}>
                    {paso > step.num ? <CheckCircle className="w-5 h-5" /> : step.num}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">{step.label}</span>
                </div>
                {idx < 3 && <div className={`flex-1 h-0.5 mx-4 ${paso > step.num ? 'bg-primary' : 'bg-muted'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Historial colapsable */}
        {mostrarHistorial && (
          <div className="bg-card rounded-lg border border-border p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <History className="w-4 h-4" />
                Historial de Importaciones
              </h3>
              <button
                onClick={cargarHistorial}
                disabled={cargandoHistorial}
                className="p-2 hover:bg-muted rounded-lg"
              >
                <RefreshCw className={`w-4 h-4 ${cargandoHistorial ? 'animate-spin' : ''}`} />
              </button>
            </div>
            
            {historial.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-4">No hay importaciones registradas</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {historial.slice(0, 10).map((h, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg text-sm">
                    <div className="flex items-center gap-3">
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      <div>
                        <p className="font-medium">{h.nombre_archivo}</p>
                        <p className="text-muted-foreground text-xs">
                          {formatDate(h.fecha)} • {h.usuario_nombre || 'Sistema'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-green-600 font-medium">
                        {formatNumber(h.registros_creados || h.tarifas_nuevas || 0)} nuevos
                      </p>
                      <p className="text-blue-600 text-xs">
                        {formatNumber(h.registros_actualizados || h.tarifas_actualizadas || 0)} actualizados
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Zona de arrastrar archivo */}
        {paso === 1 && !archivo && (
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className={`
              border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer
              ${arrastrando ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
            `}
            data-testid="dropzone"
            onClick={() => document.getElementById('file-input').click()}
          >
            <input
              id="file-input"
              data-testid="file-input"
              type="file"
              accept=".xlsx"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Upload className={`w-16 h-16 mx-auto mb-4 ${arrastrando ? 'text-primary' : 'text-muted-foreground'}`} />
            <h3 className="text-lg font-semibold mb-2">
              {arrastrando ? 'Suelte el archivo aquí' : 'Arrastre el archivo Excel aquí'}
            </h3>
            <p className="text-muted-foreground mb-4">o haga clic para seleccionar</p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 inline-block text-left max-w-lg">
              <p className="text-amber-800 font-medium text-sm mb-2">Formato requerido</p>
              <p className="text-amber-700 text-xs mb-3">
                El archivo Excel debe contener una hoja llamada <strong>TARIFICADOR</strong> con la estructura oficial de tarifas.
              </p>
              <p className="text-amber-600 text-xs mb-2">
                La importación utiliza exclusivamente la información contenida en esta hoja.
              </p>
              <p className="text-amber-600 text-xs italic">
                No se requieren hojas adicionales.
              </p>
            </div>
          </div>
        )}

        {/* Archivo seleccionado */}
        {archivo && paso < 4 && (
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FileSpreadsheet className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold">{archivo.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {(archivo.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <button
                onClick={reiniciar}
                className="text-muted-foreground hover:text-foreground"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {/* Validación */}
            {validacion && (
              <div className={`p-4 rounded-lg mb-6 ${validacion.es_valido ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {validacion.es_valido ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-semibold ${validacion.es_valido ? 'text-green-800' : 'text-red-800'}`}>
                    {validacion.es_valido ? 'Estructura válida' : 'Estructura inválida'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm mt-3">
                  <div>
                    <p className="font-medium mb-1">Hojas encontradas:</p>
                    <ul className="space-y-1">
                      {validacion.hojas_encontradas?.map(h => (
                        <li key={h} className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-3 h-3" /> {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {validacion.hojas_faltantes?.length > 0 && (
                    <div>
                      <p className="font-medium mb-1 text-red-800">Hojas faltantes:</p>
                      <ul className="space-y-1">
                        {validacion.hojas_faltantes.map(h => (
                          <li key={h} className="flex items-center gap-2 text-red-700">
                            <XCircle className="w-3 h-3" /> {h}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Previsualización */}
            {previsualizacion && (
              <div className="space-y-4">
                {/* Resumen de tarifas */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-amber-50 p-4 rounded-lg text-center">
                    <Package className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-amber-800">{formatNumber(previsualizacion.total_confecciones)}</p>
                    <p className="text-sm text-amber-600">Confecciones</p>
                  </div>
                  <div className="bg-emerald-50 p-4 rounded-lg text-center">
                    <Table2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                    <p data-testid="preview-total-tarifas" className="text-2xl font-bold text-emerald-800">{formatNumber(previsualizacion.total_tarifas)}</p>
                    <p className="text-sm text-emerald-600">Tarifas totales</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <FileSpreadsheet className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-800">{previsualizacion.tipos_detectados?.length || 0}</p>
                    <p className="text-sm text-blue-600">Tipos</p>
                  </div>
                </div>

                {/* Resumen de cambios */}
                <div className="bg-muted/50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3">Resumen de cambios</h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="bg-green-100 p-3 rounded-lg">
                      <p className="text-xl font-bold text-green-700">{formatNumber(previsualizacion.tarifas_nuevas)}</p>
                      <p className="text-xs text-green-600">Nuevas</p>
                    </div>
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <p className="text-xl font-bold text-blue-700">{formatNumber(previsualizacion.tarifas_actualizar)}</p>
                      <p className="text-xs text-blue-600">A actualizar</p>
                    </div>
                    <div className="bg-gray-100 p-3 rounded-lg">
                      <p className="text-xl font-bold text-gray-700">{formatNumber(previsualizacion.tarifas_sin_cambios)}</p>
                      <p className="text-xs text-gray-600">Sin cambios</p>
                    </div>
                  </div>
                </div>

                {/* Referencias huérfanas */}
                {previsualizacion.referencias_huerfanas > 0 && (
                  <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-amber-800">
                          {formatNumber(previsualizacion.referencias_huerfanas)} referencias existentes no están en el nuevo archivo
                        </p>
                        <p className="text-sm text-amber-700 mt-1">
                          ¿Qué desea hacer con estas referencias?
                        </p>
                        <div className="flex gap-4 mt-3">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="accion_huerfanas"
                              value="mantener"
                              checked={accionHuerfanas === 'mantener'}
                              onChange={(e) => setAccionHuerfanas(e.target.value)}
                              className="text-primary"
                            />
                            <span className="text-sm">Mantener activas</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="accion_huerfanas"
                              value="desactivar"
                              checked={accionHuerfanas === 'desactivar'}
                              onChange={(e) => setAccionHuerfanas(e.target.value)}
                              className="text-primary"
                            />
                            <span className="text-sm">Desactivar</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Configuración detectada */}
                <button
                  onClick={() => setMostrarConfiguracion(!mostrarConfiguracion)}
                  className="w-full flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                >
                  <span className="font-medium flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Configuración detectada
                  </span>
                  {mostrarConfiguracion ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                {mostrarConfiguracion && previsualizacion.configuracion && (
                  <div className="bg-muted/30 p-4 rounded-lg text-sm space-y-2">
                    <p><strong>IVA:</strong> {(previsualizacion.configuracion.iva * 100).toFixed(0)}%</p>
                    <p><strong>Multiplicador predeterminado:</strong> {previsualizacion.configuracion.multiplicador_predeterminado}x</p>
                    <p><strong>Método de precio:</strong> {previsualizacion.configuracion.metodo_precio}</p>
                    <p><strong>Forro predeterminado:</strong> ${formatNumber(previsualizacion.configuracion.forro_predeterminado)}</p>
                    <p><strong>Confecciones:</strong> {Object.keys(previsualizacion.configuracion.confecciones || {}).join(', ')}</p>
                  </div>
                )}

                {/* Tipos y tallas detectados */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium mb-1">Tipos detectados:</p>
                    <div className="flex flex-wrap gap-1">
                      {previsualizacion.tipos_detectados?.map(t => (
                        <span key={t} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Tallas detectadas:</p>
                    <div className="flex flex-wrap gap-1">
                      {previsualizacion.tallas_detectadas?.map(t => (
                        <span key={t} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Errores */}
                {previsualizacion.errores?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                    <button
                      onClick={() => setMostrarErrores(!mostrarErrores)}
                      className="flex items-center justify-between w-full"
                    >
                      <span className="font-medium text-red-800 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {previsualizacion.errores.length} errores encontrados
                      </span>
                      {mostrarErrores ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    {mostrarErrores && (
                      <div className="mt-3 max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="text-left">
                            <tr className="border-b border-red-200">
                              <th className="pb-1">Hoja</th>
                              <th className="pb-1">Fila</th>
                              <th className="pb-1">Campo</th>
                              <th className="pb-1">Problema</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previsualizacion.errores.slice(0, 20).map((err, idx) => (
                              <tr key={idx} className="border-b border-red-100">
                                <td className="py-1">{err.hoja}</td>
                                <td className="py-1">{err.fila}</td>
                                <td className="py-1">{err.campo}</td>
                                <td className="py-1">{err.problema}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Botones de acción */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              {paso === 1 && (
                <button
                  data-testid="validar-btn"
                  onClick={validarArchivo}
                  disabled={cargando}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                >
                  {cargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4" />}
                  Validar Estructura
                </button>
              )}
              
              {paso === 2 && validacion?.es_valido && (
                <button
                  data-testid="previsualizar-btn"
                  onClick={previsualizarCambios}
                  disabled={cargando}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                >
                  {cargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  Generar Vista Previa
                </button>
              )}
              
              {paso === 3 && previsualizacion && (
                <button
                  data-testid="confirmar-importacion-btn"
                  onClick={confirmarImportacion}
                  disabled={cargando}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {cargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Confirmar Importación
                </button>
              )}
            </div>
          </div>
        )}

        {/* Resultado final */}
        {resultado && paso === 4 && (
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-green-800">Importación Exitosa</h3>
              <p className="text-muted-foreground mt-1">{resultado.mensaje}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-green-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-700">{formatNumber(resultado.registros_creados)}</p>
                <p className="text-sm text-green-600">Creados</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-700">{formatNumber(resultado.registros_actualizados)}</p>
                <p className="text-sm text-blue-600">Actualizados</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-gray-700">{formatNumber(resultado.registros_omitidos)}</p>
                <p className="text-sm text-gray-600">Sin cambios</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-700">{formatNumber(resultado.registros_con_error)}</p>
                <p className="text-sm text-red-600">Errores</p>
              </div>
            </div>

            <div className="flex justify-center gap-4">
              <button
                onClick={reiniciar}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Nueva Importación
              </button>
              <button
                onClick={() => navigate('/quotes')}
                className="px-6 py-2 border border-border rounded-lg hover:bg-muted flex items-center gap-2"
              >
                Ir a Cotizaciones
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CargarTarifasExcel;
